import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import config from '@/config'
import { safeLocalStorageGet } from '@/utils'

// 请求重试配置
const retryConfig = config.retry || {
  maxRetries: 2,
  retryDelay: 1000,
  retryableStatuses: [408, 429, 500, 502, 503, 504]
}

// 重试计数器
const retryCountMap = new Map()

// 获取请求标识
function getRequestId(config) {
  return `${config.method}-${config.url}`
}

// 检查是否需要重试
function shouldRetry(error) {
  // 网络错误或超时
  if (!error.response) {
    return true
  }
  // 特定的状态码
  const status = error.response.status
  return retryConfig.retryableStatuses.includes(status)
}

// 延迟函数
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 创建axios实例
const service = axios.create({
  baseURL: config.baseURL,
  timeout: config.timeout // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    // 优先从store获取token，若无则安全读取 localStorage（避免跟踪防护阻断时抛错）
    let token = store.getters['auth/token']
    if (!token || token.trim() === '') {
      token = safeLocalStorageGet('token') || ''
      // 如果从localStorage读取到token，同步到store
      if (token) {
        store.commit('auth/SET_TOKEN', token)
      }
    }
    if (token && token.trim() !== '') {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 对请求错误做些什么
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 对响应数据做点什么
    const res = response.data

    // FastAPI默认返回状态码200，检查HTTP状态码
    // 清理重试计数
    const requestId = getRequestId(response.config)
    retryCountMap.delete(requestId)
    
    if (response.status >= 200 && response.status < 300) {
      const url = response.config?.url || ''

      // 专家分类API直接返回数据，不检查code字段
      if (url.includes('/expert-categories')) {
        console.log('🔍 [PC Request] 专家分类API，直接返回数据')
        console.log('🔍 [PC Request] 原始响应数据:', res)
        console.log('🔍 [PC Request] 响应数据类型:', typeof res)
        console.log('🔍 [PC Request] 是否为数组:', Array.isArray(res))
        return res
      }

      // 仅当 code 是“数字类型业务码”时，才按业务码判断
      // 20000 表示成功（后端标准成功码），200 也表示成功，其他码表示错误
      if (typeof res?.code === 'number' && res.code !== 200 && res.code !== 20000) {
        Message({
          message: res.message || '请求失败',
          type: 'error',
          duration: 5 * 1000
        })

        // 401: 未授权，需要重新登录
        // 注意：不要立即清除登录状态，让调用方决定如何处理
        // 某些接口的401可能是正常的（如未登录访问需要登录的接口）
        if (res.code === 401) {
          // 只对特定的认证相关接口清除登录状态
          // 并且只有在明确是认证失败时才清除（不是网络问题）
          const url = response.config?.url || ''
          if (url.includes('/auth/user') || url.includes('/auth/heartbeat')) {
            const errorMessage = res.message || res.detail || ''
            const isAuthFailure = errorMessage.includes('token') || 
                                  errorMessage.includes('认证') || 
                                  errorMessage.includes('登录') ||
                                  errorMessage.includes('unauthorized') ||
                                  errorMessage.includes('expired') ||
                                  errorMessage === '' // 空消息也可能是认证失败
            if (isAuthFailure) {
              store.dispatch('auth/logout')
              location.reload()
            }
          }
        }

        return Promise.reject(new Error(res.message || '请求失败'))
      }

      // 返回数据：
      // - 若是标准包装 { code:number, data:any, message } 则直接返回
      // - 若是原始对象且有 data 字段，返回 data
      // - 否则直接返回原始对象（不进行额外包装）
      if (typeof res?.code === 'number') {
        return res
      }
      // 直接返回原始响应数据，不进行额外包装
      return res
    } else {
      Message({
        message: '请求失败',
        type: 'error',
        duration: 5 * 1000
      })
      return Promise.reject(new Error('请求失败'))
    }
  },
  async error => {
    // 对响应错误做点什么
    
    // 重试逻辑
    const requestId = getRequestId(error.config)
    const currentRetryCount = retryCountMap.get(requestId) || 0
    
    if (shouldRetry(error) && currentRetryCount < retryConfig.maxRetries) {
      retryCountMap.set(requestId, currentRetryCount + 1)
      console.log(`🔍 [Request] 请求失败，${currentRetryCount + 1}/${retryConfig.maxRetries} 秒后重试:`, error.config.url)
      
      await delay(retryConfig.retryDelay)
      
      // 重置超时时间
      const newConfig = { ...error.config }
      newConfig.timeout = config.timeout
      
      return service.request(newConfig)
    }
    
    // 清理重试计数
    retryCountMap.delete(requestId)
    
    // 对于认证相关的403/401错误，静默处理（用户未登录是正常情况）
    const isAuthError = error.config?.url?.includes('/auth/user') && (error.response?.status === 401 || error.response?.status === 403);
    const isHeartbeatError = error.config?.url?.includes('/auth/heartbeat') && (error.response?.status === 401 || error.response?.status === 403);
    const isNotificationError = error.config?.url?.includes('/notifications/unread-count') && (error.response?.status === 401 || error.response?.status === 403);
    // 对于案例编辑状态API的404错误，静默处理（某些案例可能没有编辑状态是正常情况）
    const isCaseEditStatus404 = error.config?.url?.includes('/cases/my/') && error.config?.url?.includes('/edit-status') && error.response?.status === 404;
    // 对于案例更新API的404错误，由组件自己处理，不显示全局错误消息（避免重复显示）
    const isCaseUpdate404 = error.config?.url?.includes('/cases/my/') && error.config?.method === 'put' && error.response?.status === 404;
    // 警示教育焦点视频 list 接口 404 时静默（组件会回退到 /focus-video/current）
    const isFocusVideoList404 = error.config?.url?.includes('/focus-video/list') && error.response?.status === 404;
    // 警示教育匹配法规接口 404 时静默（后端未提供时组件用空列表兜底）
    const isMatchedRegulations404 = error.config?.url?.includes('/focus-video/matched-regulations') && error.response?.status === 404;
    // 首页列表接口（应急场景/标准/案例/领导关怀新闻）：仅 GET 列表失败时静默，不弹窗，组件已用空数据兜底
    const url = error.config?.url || ''
    const method = (error.config?.method || 'get').toUpperCase()
    const isHomeListApi = method === 'GET' && (
      url.includes('/scenarios') && !url.includes('/scenarios/') ||
      url.includes('/standards') && !url.includes('/standards/') && !url.includes('/detail/') ||
      (url === '/cases' || url.startsWith('/cases?')) ||
      (url.includes('/leadership-care') && !url.includes('/admin') && !/\/leadership-care\/\d+/.test(url))
    )

    if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
      const status = error.response?.status
      const statusText = error.response?.statusText || ''
      const data = error.response?.data
      console.error('🔍 [Request] Response error:', error.message, '|', method, url, '→', status || 'no response', statusText, data ? JSON.stringify(data).slice(0, 200) : '')
      console.error('🔍 [Request] 错误详情:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config,
        stack: error.stack
      })
    }

    let message = '网络错误'
    
    // 处理超时错误
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      message = '请求超时，请检查网络连接或稍后重试'
      if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
        Message({
          message,
          type: 'error',
          duration: 5 * 1000
        })
      }
      return Promise.reject(error)
    }
    
    if (error.response) {
      if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
        console.log('🔍 [Request] 响应错误详情:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers,
          config: error.response.config
        })
      }
      
      // 优先显示后端返回的具体错误信息
      // 只有当HTTP状态码不是2xx时才显示错误信息
      if (error.response.status >= 400) {
        if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
          console.log('🔍 [Request] HTTP状态码>=400，处理错误信息')
        }
        if (error.response.data?.message) {
          message = error.response.data.message
          if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
            console.log('🔍 [Request] 使用后端返回的message:', message)
          }
        } else if (error.response.data?.detail) {
          // 处理422错误的detail字段（可能是数组或字符串）
          const detail = error.response.data.detail
          if (Array.isArray(detail) && detail.length > 0) {
            // FastAPI验证错误通常是数组格式
            message = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ')
          } else if (typeof detail === 'string') {
            message = detail
          } else {
            message = JSON.stringify(detail)
          }
          // 针对账号未审核/已禁用导致的403，统一给出更友好的提示
          const detailStr = String(error.response.data.detail || '')
          if (
            error.response.status === 403 &&
            (detailStr.includes('尚未通过管理员审核') || detailStr.includes('已被禁用'))
          ) {
            message = '请等待管理员审核认证通过该账号操作，如需加急请联系管理员。'
          }
          if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
            console.log('🔍 [Request] 使用后端返回的detail:', message)
          }
        } else {
          // 如果没有具体错误信息，则使用默认消息
          switch (error.response.status) {
        case 400:
          message = '请求错误'
          break
        case 401:
          message = '未授权' // 不显示"请重新登录"提示
          // /auth/user、/auth/heartbeat 返回 401 时一律清除登录状态（含用户已删除、token 过期等）
          const url = error.config?.url || ''
          if (url.includes('/auth/user') || url.includes('/auth/heartbeat')) {
            store.dispatch('auth/logout')
          }
          break
        case 403:
          message = '拒绝访问'
          break
        case 404:
          // 根据请求路径判断是账号不存在还是地址错误
          if (error.config?.url?.includes('/auth/login')) {
            message = '账号未注册，请先注册'
          } else {
            message = '请求地址出错'
          }
          break
        case 408:
          message = '请求超时'
          break
        case 500:
          message = '服务器内部错误'
          break
        case 501:
          message = '服务未实现'
          break
        case 502:
          message = '网关错误'
          break
        case 503:
          message = '服务不可用'
          break
        case 504:
          message = '网关超时'
          break
        case 505:
          message = 'HTTP版本不受支持'
          break
        default:
          message = `连接错误${error.response.status}`
          }
        }
      } else {
        // HTTP状态码是2xx，但axios认为这是错误，可能是其他问题
        console.log('🔍 [Request] HTTP状态码是2xx，但axios认为这是错误')
        message = '请求处理异常'
      }
    } else {
      console.log('🔍 [Request] 没有响应对象，可能是网络问题')
      message = '连接到服务器失败'
    }
    
    if (!isAuthError && !isHeartbeatError && !isNotificationError && !isCaseEditStatus404 && !isCaseUpdate404 && !isFocusVideoList404 && !isMatchedRegulations404) {
      console.log('🔍 [Request] 最终错误消息:', message)
    }

    // 显示错误消息
    // 对于登录页面的错误，让登录页面自己处理，避免重复显示
    // 对于心跳、通知API的错误，静默处理
    // 对于401错误，不显示提示信息，静默处理
    if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 404)) {
      // 如果是登录请求，不在这里显示错误信息，让登录页面处理
      if (error.config?.url?.includes('/auth/login')) {
        // 登录页面的错误由登录页面自己处理，这里不显示
        return Promise.reject(error)
      }
      
      // 401错误不显示提示，静默处理
      if (error.response.status === 401) {
        return Promise.reject(error)
      }
      
      // 心跳、通知API和案例编辑状态API的错误静默处理
      // 案例更新API的404错误由组件自己处理，这里不显示（避免重复显示）
      // 警示教育焦点视频 list 的 404 由组件回退到 current，不弹窗
      // 警示教育匹配法规 404 由组件用空列表兜底，不弹窗
      // 首页列表接口（应急场景/标准/案例）失败时静默，页面已用空数据兜底
      if (isHeartbeatError || isNotificationError || isCaseEditStatus404 || isCaseUpdate404 || isFocusVideoList404 || isMatchedRegulations404 || isHomeListApi) {
        return Promise.reject(error)
      }
      
      Message({
        message,
        type: 'error',
        duration: 5 * 1000
      })
    }

    return Promise.reject(error)
  }
)

export default service